<script setup>

import { use } from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { PieChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GraphicComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'
import { ref, provide, computed, watch, useTemplateRef, onMounted, onUpdated } from 'vue'
import MetricCardTitle from '@/views/dashboard/components/MetricCardTitle.vue'

defineOptions({
  name: 'DonutMetricCard'
})
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  unit: {
    type: String,
    required: true
  },
  total: {
    type: Number,
    required: true
  },
  value: {
    type: Number,
    required: true
  },
  growth: {
    type: Number,
    required: true
  },
  tooltip: {
    type: String
  }
})
const pieChart = useTemplateRef('pieChart')
const currentSelected = ref('visited');
// 格式化数字为千分位
const numberFormat = new Intl.NumberFormat('en-US')
const formattedTotal = computed(() => {
  return numberFormat.format(props.value)
})
use([
  SVGRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GraphicComponent
])

// 计算选中项的百分比
const getPercentage = () => {
  if (props.total === 0) return '0.0';
  const value = currentSelected.value === 'visited' ? props.value : props.total - props.value;
  // 先计算百分比再保留两位小数
  return ((value / props.total) * 100).toFixed(1);
};

// provide(THEME_KEY, 'dark')
const option = ref({
  // tooltip: {
    // trigger: 'item',
    // formatter: '{c} ({d}%)'
  // },
  color: ['#634075', '#EBEFEE'],
  series: [
    {
      name: 'Access From',
      type: 'pie',
      radius: ['50%', '90%'],
      avoidLabelOverlap: false,
      label: {
        show: true,
        position: 'center',
        color: '#3C1053',
        fontSize: 18,
        fontWeight: 'bold',
        formatter: () => {
          return `${getPercentage()}%`;
        }
      },
      emphasis: {
        itemStyle: {
          color: 'inherit'
        }
      },
      labelLine: {
        show: false
      },
      data: []
    }
  ]
})
const growthClass = computed(() => {
  if (props.growth > 0) {
    return 'up'
  } else if (props.growth < 0) {
    return 'down'
  } else {
    return 'equal'
  }
})

watch(props, (newValue, oldValue) => {
  pieChart.value.chart.setOption({
    series: [
      {
        data: [
          { value: props.value, name: 'visited' },
          { value: props.total - props.value, name: 'unvisited' }
        ]
      }]
  })
})

onMounted(() => {
  initChart()
})

const initChart = () => {
  if (!pieChart.value) return;

  // 鼠标移入事件
  pieChart.value.chart.on('mouseover', (params) => {
    const newSelected = {
      'visited': false,
      'unvisited': false
    };

    if (params.name === currentSelected.value) {
      currentSelected.value = 'visited';
      newSelected.visited = true;
    } else {
      currentSelected.value = params.name;
      newSelected[params.name] = true;
    }
    updateChartSelection(newSelected);
  })

  // 鼠标移出事件 - 重置为显示visited
  pieChart.value.chart.on('mouseout', () => {
    const defaultSelected = {
      'visited': true,
      'unvisited': false
    };
    currentSelected.value = 'visited';
    updateChartSelection(defaultSelected);
  });

}


const updateChartSelection = (selectedState) => {
  pieChart.value.chart.setOption({
    series: [{
      selected: selectedState,
      label: {
        formatter: () => `${getPercentage()}%`
      }
    }]
  });
};

</script>

<template>
  <div class="donut-metric-card">
    <div class="donut-metric-card__header">
      <MetricCardTitle :title="title" :tooltip="tooltip" />
    </div>
    <div class="donut-metric-card__body">
      <v-chart ref="pieChart" class="chart" :option="option" autoresize />
    </div>
    <div class="donut-metric-card__footer">
      <span class="text">{{ unit }}</span>
      <span class="number">{{ formattedTotal }}</span>
      <div class="growth" :class="growthClass">
        <span>{{ props.growth ? props.growth : 0 }}%</span>
        <svg class="icon" aria-hidden="true">
          <use :xlink:href="props.growth > 0 ? '#icon-arrow_up' : '#icon-arrow_down'"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.donut-metric-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  &__header {}

  &__body {
    flex-shrink: 0;
  }

  &__footer {
    display: flex;
    gap: var(--space-sm);
    font-size: 1rem;
    align-items: center;
    flex-wrap: nowrap;

    .text {
      flex-shrink: 0;
      color: var(--color-text);
    }

    .number {
      flex-shrink: 0;
      font-weight: 800;
      color: var(--color-primary-dark);
    }

    .growth {
      flex-shrink: 0;

      * {
        font-weight: 800;
      }
    }
  }
}

.up {
  color: var(--color-primary);
}

.down {
  color: var(--az-color-green-60);
}

.equal {
  color: var(--az-color-gray-100);

  .icon {
    transform: rotate(270deg);
  }
}

.chart {
  height: 10rem;
  width: 10rem;
}
</style>
